<template>
  <div class="question grid md:grid-cols-2 gap-y-10">
    <div class="left md:pr-20">
      <transition-group name="fade" tag="div" mode="in-out" class="relative">
        <img
          v-if="question.image"
          v-lazy="config.baseUrl + '/storage/' + question.image"
          :alt="question.text"
          :key="question.image + 'single'"
          class="block object-cover w-full"
        />
        <img
          v-for="(item, index) in answersWithImage"
          v-show="item.image && (showImage(item) || item.showImage)"
          v-lazy="config.baseUrl + '/storage/' + item.image"
          :alt="item.text"
          :key="item.uuid + 'multi' + index"
          class="block object-cover absolute inset-0 w-full"
        />
      </transition-group>
    </div>
    <div class="right">
      <div>
        <span v-if="page" class="font-normal text-sm"
          >{{ snippets.question }} {{ page }} {{ snippets.question_of }} {{ pages }}</span
        >
      </div>
      <base-headline level="1">
        {{ replacedText }}
      </base-headline>
      <div>
        <base-text v-if="question.help" class="explaination mt-5">
          {{ question.help }}
        </base-text>
        <module-answer
          :question-id="question.uuid"
          :items="question.answers"
          :type="question.type"
          class="answers mt-5 text-base md:text-lg"
          @change-image="changeImage"
          @select-image="selectImage"
        />
      </div>
      <module-navigation class="mt-10 md:mt-20 hidden md:block" />
    </div>
  </div>
</template>
<script lang="ts">
import ModuleNavigation from '../components/module/ModuleNavigation.vue'
import { computed, defineComponent, inject, ref, watchEffect, watch } from 'vue'
import { useMainStore } from '@/store/main'
import { Question } from '@/types/question'
import BaseHeadline from '@/components/base/BaseHeadline.vue'
import BaseText from '@/components/base/BaseText.vue'
import ModuleAnswer from '@/components/module/ModuleAnswer.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import { Answer, Types } from '@/types/answer'
import { Language } from '@/types/language'
import { Replacer } from '@/utils'

export default defineComponent({
  components: {
    BaseHeadline,
    ModuleAnswer,
    BaseSelect,
    BaseText,
    ModuleNavigation,
  },

  setup() {
    /**
     * rgisster Store
     */
    const store = useMainStore()
    /**
     * get config
     */
    const config: any = inject('config')

    /**
     * @returns {Question} question
     */
    const question = computed((): Question => store.question)

    const selectedAnswers = computed((): String[] => store.answer)

    const snippets = computed((): Language => store.snippets)

    /**
     * dynamic images set on hover or select
     */
    const hoveredAnswer = ref<Answer | null>(null)
    const lastSelectedAnswer = ref<Answer | null>(null)

    /**
     * change the image on answer hover
     */
    const changeImage = (answer: Answer) => {
      hoveredAnswer.value = answer
    }
    /**
     * change the image on answer hover
     */
    const selectImage = (answer: Answer) => {
      lastSelectedAnswer.value = answer
    }

    /**
     * @returns {number} page
     */
    const page = computed(() => store.page)
    /**
     * @returns {number} pages
     */
    const pages = computed(() => store.pages)

    /**
     * @returns {Answer[]} answersWithImage
     */
    const answersWithImage = computed(() => {
      return {
        showImage: false,
        ...question.value.answers.filter((answer: Answer) => {
          return answer.image
        }),
      }
    })

    /**
     * @returns {boolean} showImage
     */
    function showImage(item: Answer) {
      item.showImage =
        item.uuid === lastSelectedAnswer.value?.uuid ||
        item.uuid === hoveredAnswer.value?.uuid ||
        selectedAnswers.value.includes(item.uuid)
      return item.showImage
    }

    /**
     * computed replaced text depend on config values
     */
    const replacedText = computed(() => {
      return Replacer(question.value.text)
    })
    return {
      question,
      replacedText,
      Types,
      config,
      changeImage,
      selectImage,
      answersWithImage,
      showImage,
      page,
      pages,
      snippets,
    }
  },
})
</script>
<style scoped>
.image + .content {
  @apply mt-10 md:mt-0;
}

.fade-enter,
.fade-leave-to {
  @apply opacity-0;
}
.fade-enter-active,
.fade-leave-active {
  @apply transition ease-in-out duration-150;
}
</style>
